/**
 * src/components/ColorPicker.jsx — accent colour chooser.
 *
 * Deliberately not `<input type="color">`.
 *
 * On macOS that input opens the system colour panel (NSColorPanel), which is a
 * floating window rather than a dropdown. It has no notion of "you picked one,
 * I'll close now": it stays up until the user dismisses it, and it floats over
 * the rest of the form — including the Save button. The result is a settings
 * page that appears stuck. That is the OS behaving as designed, so there is no
 * way to close it from script; the only fix is not to open it.
 *
 * Swatches plus a hex field cover the actual need better anyway. A studio picks
 * a brand colour once, and most of the time it is one of a handful of sensible
 * dark tones — or a specific hex from a brand guide, which is far easier to
 * paste than to hunt for in a colour wheel.
 */
import { useId, useState } from 'react';

/**
 * Preset accent colours.
 *
 * Chosen to work as the accent in the booking portal and in emails: each is
 * dark enough that white text on it stays legible, which the palette in
 * styles.css assumes for `.btn-primary`.
 */
const PRESETS = [
  { hex: '#1a1a1a', name: 'Ink black' },
  { hex: '#3d3d3d', name: 'Charcoal' },
  { hex: '#7a2e2e', name: 'Oxblood' },
  { hex: '#b23a48', name: 'Crimson' },
  { hex: '#8a5a2b', name: 'Tan' },
  { hex: '#2f6f4f', name: 'Forest' },
  { hex: '#2c5d7c', name: 'Slate blue' },
  { hex: '#3f3d6b', name: 'Indigo' },
  { hex: '#6b3f5f', name: 'Plum' },
  { hex: '#5c6b3f', name: 'Olive' },
];

/** Matches #rgb and #rrggbb, the two forms CSS and the API both accept. */
const HEX_PATTERN = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

/**
 * Accent colour picker.
 *
 * @param {object} props
 * @param {string} props.value Current colour as a hex string.
 * @param {(hex: string) => void} props.onChange Called with a valid hex only.
 * @param {string} [props.label]
 * @returns {JSX.Element}
 */
export default function ColorPicker({ value, onChange, label = 'Accent colour' }) {
  const id = useId();
  // The typed value is tracked separately from the committed one so that a
  // half-finished hex ("#b2") does not repaint the preview or get saved, while
  // still letting the field show what is being typed.
  const [draft, setDraft] = useState(null);
  const shown = draft ?? value;
  const invalid = draft !== null && !HEX_PATTERN.test(draft);

  /**
   * @param {string} next
   */
  const commit = (next) => {
    setDraft(null);
    onChange(next.toLowerCase());
  };

  return (
    <div className="stack" style={{ gap: 8 }}>
      <span className="color-picker-label">{label}</span>

      <div className="row" style={{ gap: 10 }}>
        <span
          className="color-preview"
          style={{ background: HEX_PATTERN.test(shown) ? shown : 'transparent' }}
          aria-hidden="true"
        />
        <input
          id={id}
          aria-label="Hex"
          value={shown}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => {
            // Snap back to the saved colour rather than leaving something
            // unparseable in the field.
            if (draft !== null && HEX_PATTERN.test(draft)) commit(draft);
            else setDraft(null);
          }}
          spellCheck="false"
          aria-invalid={invalid}
          style={{ width: 120, fontFamily: 'ui-monospace, monospace' }}
        />
      </div>

      {invalid ? (
        <span className="small" style={{ color: 'var(--danger)' }}>
          Use a hex colour like #1a1a1a.
        </span>
      ) : null}

      <div className="row wrap" style={{ gap: 6 }} role="group" aria-label="Preset colours">
        {PRESETS.map((preset) => {
          const selected = shown.toLowerCase() === preset.hex;
          return (
            <button
              key={preset.hex}
              type="button"
              className={`color-swatch${selected ? ' selected' : ''}`}
              style={{ background: preset.hex }}
              onClick={() => commit(preset.hex)}
              title={`${preset.name} · ${preset.hex}`}
              aria-label={preset.name}
              aria-pressed={selected}
            />
          );
        })}
      </div>
    </div>
  );
}
